/**
 * Strategy Slide
 *
 * Slide 4: "The strategy we chose" - Shows the selected ad strategy.
 */

import { motion } from 'framer-motion'
import { Compass, Quote, Zap, CheckCircle } from 'lucide-react'
import type { OnboardingArtifacts } from '../../../contexts/OnboardingContext'

interface StrategySlideProps {
  artifacts: OnboardingArtifacts
}

export default function StrategySlide({ artifacts }: StrategySlideProps) {
  // Handle nested strategy object
  const strategyData = artifacts.strategy as {
    strategy_name?: string
    strategy_description?: string
    rationale?: string
    hook_pattern?: string
    proof_pattern?: string
    cta_pattern?: string
    angle?: string
    strategy?: {
      source?: string
      hook_pattern?: string
      proof_pattern?: string
      offer_pattern?: string
      cta_pattern?: string
      rationale?: string
    }
  } | null

  const nestedStrategy = strategyData?.strategy

  const strategyName = strategyData?.strategy_name || nestedStrategy?.source || 'Conversion-Optimized'
  const description = strategyData?.strategy_description || 'A proven approach designed to maximize engagement and drive purchases'
  const rationale = strategyData?.rationale || nestedStrategy?.rationale || 'Based on top-performing ads in your industry'
  const hookPattern = strategyData?.hook_pattern || nestedStrategy?.hook_pattern || 'Problem-Solution'
  const proofPattern = strategyData?.proof_pattern || nestedStrategy?.proof_pattern || 'Social Proof'
  const ctaPattern = strategyData?.cta_pattern || nestedStrategy?.cta_pattern || 'Urgency'

  const features = [
    { label: 'Hook', value: hookPattern },
    { label: 'Proof', value: proofPattern },
    { label: 'CTA', value: ctaPattern },
  ]

  return (
    <div className="flex flex-col items-center text-center">
      {/* Icon */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, type: 'spring' }}
        className="w-20 h-20 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center mb-8 shadow-lg"
      >
        <Compass className="w-10 h-10 text-white" />
      </motion.div>

      {/* Title */}
      <motion.h2
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-3xl font-bold text-tf-text mb-2"
      >
        Your Ad Strategy
      </motion.h2>

      {/* Strategy name */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-indigo-500/10 text-indigo-400 mb-4"
      >
        <Zap className="w-4 h-4" />
        <span className="font-semibold">{strategyName}</span>
      </motion.div>

      {/* Description */}
      <motion.p
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="text-tf-text-muted max-w-md mb-8"
      >
        {description}
      </motion.p>

      {/* Strategy features */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="flex items-center gap-4 mb-8"
      >
        {features.map((feature, index) => (
          <motion.div
            key={feature.label}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.7 + index * 0.1 }}
            className="flex flex-col items-center px-4 py-3 rounded-xl bg-tf-bg-muted border border-tf-border"
          >
            <CheckCircle className="w-5 h-5 text-emerald-400 mb-1" />
            <span className="text-xs text-tf-text-muted">{feature.label}</span>
            <span className="text-sm font-medium text-tf-text">{feature.value}</span>
          </motion.div>
        ))}
      </motion.div>

      {/* Rationale quote */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 1 }}
        className="max-w-md"
      >
        <div className="relative px-6 py-4 rounded-xl bg-tf-bg-muted border border-tf-border">
          <Quote className="absolute -top-2 -left-2 w-6 h-6 text-indigo-400 bg-tf-bg-card rounded-full p-1" />
          <p className="text-sm text-tf-text-muted italic">
            "{rationale}"
          </p>
        </div>
      </motion.div>
    </div>
  )
}
